"use client";

import { Check } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import { CcLandingAbView, CcLandingSpeedView } from "./CcLandingAnatomyViews";
import { CcLandingMock } from "./CcLandingMock";
import type { LandingMockPart, LandingSubpageCopy } from "./landing-page-copy";

type CcLandingAnatomyProps = {
  copy: LandingSubpageCopy["anatomy"];
  mock: LandingSubpageCopy["mock"];
};

/**
 * "What lifts conversion": the example page stays pinned while the steps
 * scroll past, and the page highlights the part each step talks about. A
 * checklist under the page fills up as the reader gets further. All step
 * text is plain HTML; the scroll only picks which step is active.
 */
export function CcLandingAnatomy({ copy, mock }: CcLandingAnatomyProps) {
  const [active, setActive] = useState(0);
  const stepRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(Number((entry.target as HTMLElement).dataset.index));
          }
        }
      },
      { rootMargin: "-45% 0px -45% 0px" },
    );
    for (const step of stepRefs.current) {
      if (step) observer.observe(step);
    }
    return () => observer.disconnect();
  }, []);

  const focus = copy.steps[active]?.id as LandingMockPart | undefined;
  // Speed and A/B steps swap the desktop page for their own view.
  const view = focus === "speed" ? "speed" : focus === "test" ? "test" : "page";

  return (
    <section
      className="cc-section cc-lp-anatomy"
      id="landing-anatomy"
      aria-labelledby="cc-lp-anatomy-title"
      data-cc-nav="light"
      {...devSectionAttr("landing-anatomy")}
    >
      <div className="cc-lp-anatomy__container">
        <header className="cc-lp-anatomy__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-lp-anatomy-title" className="cc-lp-anatomy__title">
            {copy.title}
          </h2>
          <p className="cc-lp-anatomy__intro">{copy.intro}</p>
        </header>

        <div className="cc-lp-anatomy__layout">
          <div className="cc-lp-anatomy__visual">
            <div className="cc-lp-anatomy__sticky">
              <div className="cc-lp-anatomy__views" data-view={view}>
                <div className="cc-lp-anatomy__view cc-lp-anatomy__view--page">
                  <CcLandingMock copy={mock} state="after" focus={view === "page" ? (focus ?? null) : null} />
                </div>
                <div className="cc-lp-anatomy__view cc-lp-anatomy__view--speed">
                  <CcLandingSpeedView copy={copy} mock={mock} />
                </div>
                <div className="cc-lp-anatomy__view cc-lp-anatomy__view--test">
                  <CcLandingAbView copy={copy} mock={mock} />
                </div>
              </div>
              <ol className="cc-lp-anatomy__checks" aria-hidden="true">
                {copy.steps.map((step, index) => (
                  <li key={step.id} data-done={index <= active ? "true" : undefined}>
                    <Check size={11} strokeWidth={3} />
                    <span>{String(index + 1).padStart(2, "0")}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <ol className="cc-lp-anatomy__steps">
            {copy.steps.map((step, index) => (
              <li
                key={step.id}
                ref={(node) => {
                  stepRefs.current[index] = node;
                }}
                data-index={index}
                data-active={index === active ? "true" : undefined}
                className="cc-lp-anatomy__step"
                style={{ "--cc-lp-i": index } as CSSProperties}
              >
                <span className="cc-lp-anatomy__num">{String(index + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
                {/* Phones: the pinned page isn't visible here, so these steps show their view inline. */}
                {step.id === "speed" ? (
                  <div className="cc-lp-anatomy__inline">
                    <CcLandingSpeedView copy={copy} mock={mock} />
                  </div>
                ) : null}
                {step.id === "test" ? (
                  <div className="cc-lp-anatomy__inline">
                    <CcLandingAbView copy={copy} mock={mock} />
                  </div>
                ) : null}
              </li>
            ))}
            <li className="cc-lp-anatomy__cta">
              <CcButton bookCallPopup bookCallVariant="landing" label={copy.cta} />
              <small>{copy.ctaNote}</small>
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
}
